import * as React from 'react'
import { AnimationDefinition } from '@ds/motion'

/**
 * Manages animation status and invokes callbacks when animations are complete.
 *
 * @param onHideComplete - Callback for when the hide animation completes.
 * @param onShowComplete - Callback for when the show animation completes.
 * @returns An object containing animation status and handlers.
 */
export function useAnimationStatus(
  onHideComplete?: () => void,
  onShowComplete?: () => void,
  hideDefinitions = ['exitOpacity'],
  showDefinitions = ['enterOpacity'],
) {
  const [animationDone, setAnimationDone] = React.useState(true)
  const [showComplete, setShowComplete] = React.useState(true)
  const [hideComplete, setHideComplete] = React.useState(true)

  const onAnimationStart = React.useCallback(() => {
    setAnimationDone(false)

    setShowComplete(false)
    setHideComplete(false)
  }, [])

  const onAnimationEnd = React.useCallback(
    (definition: AnimationDefinition) => {
      setAnimationDone(true)

      /**
       * This checks which definition (motion variant name) has completed
       * and calls the appropriate callback
       */
      if (showDefinitions.includes(definition)) {
        onShowComplete?.()
        setShowComplete(true)
      }
      if (hideDefinitions.includes(definition)) {
        onHideComplete?.()
        setHideComplete(true)
      }
    },
    [onHideComplete, onShowComplete, hideDefinitions, showDefinitions],
  )

  return {
    animationDone,
    hideComplete,
    onAnimationEnd,
    onAnimationStart,
    showComplete,
  } as const
}
